import { useEffect, useState } from "react";
import { App } from "@/App";
import { AppProvider } from "@/context/AppContext";
import { loadRuntimeConfig } from "@/config/loadConfig";
import { initI18n } from "@/i18n";
import { installGtm } from "@/analytics";
import { applyTheme, readStoredTheme } from "@/theme";
import { logger } from "@/logging";
import type { RuntimeConfig } from "@/types";

export function Bootstrap() {
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadRuntimeConfig()
      .then(async (runtime) => {
        applyTheme(readStoredTheme(runtime.app.defaultTheme));
        await initI18n(runtime.app);
        installGtm(runtime.app.gtmId);
        if (!cancelled) setConfig(runtime);
      })
      .catch((reason: unknown) => {
        const message = reason instanceof Error ? reason.message : "Failed to start";
        logger.fatal({ message: "Application bootstrap failed", error: message });
        if (!cancelled) setError(message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <main className="min-h-screen page-bg flex items-center justify-center px-6">
        <p role="alert">Unable to load the deployment wizard. {error}</p>
      </main>
    );
  }

  if (!config) {
    return (
      <main className="min-h-screen page-bg flex items-center justify-center">
        <p>Loading…</p>
      </main>
    );
  }

  return (
    <AppProvider config={config}>
      <App />
    </AppProvider>
  );
}
